import React, { useEffect, useState, useCallback } from 'react';

import { hasRoles, EDIT_SECURITY_QUESTION_ROLES } from '../../../util/RoleUtil';

import { createSecurityQuestion, viewAllSecurityQuestions } from '../../../services/SecurityQuestionsService';

import { SaveButton } from '../../common/button/save-button/SaveButton';
import { ToolTip } from '../../common/tool-tip/ToolTip';

import './ClientSecurityQuestions.css';

export const ClientSecurityQuestions = ({ context, setSpinnerText, showToaster, toolTipsEnabled, editMode }) => {
  const [securityQuestions, setSecurityQuestions] = useState([{ question: '', active: true }]);

  const retrieveSecurityQuestions = useCallback(async () => {
    setSpinnerText('Retrieving Security Questions');
    const [data, error] = await viewAllSecurityQuestions(context.username);
    if (data) {
      if (data.responseStatus === 'Success') {
        if (data.securityQuestions.length > 0) {
          setSecurityQuestions(data.securityQuestions);
        }
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  }, [context.username, setSpinnerText, showToaster]);

  useEffect(() => {
    retrieveSecurityQuestions();
  }, [retrieveSecurityQuestions]);

  const handleSaveChangesClick = () => {
    const formattedSecurityQuestions = securityQuestions.map((question) => ({
      active: question.active || false,
      question: question.question,
      externalReference: question.externalReference || null,
    }));
    const securityQuestionsToPersist = {
      securityQuestions: formattedSecurityQuestions,
      username: context.username,
    };
    saveSecurityQuestions(securityQuestionsToPersist);
  };

  const saveSecurityQuestions = async (securityQuestionsToPersist) => {
    setSpinnerText('Saving Security Questions');
    const [data, error] = await createSecurityQuestion(securityQuestionsToPersist);
    if (data) {
      showToaster({
        responseStatus: data.responseStatus,
        responseMessage: data.responseMessage,
      });
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const handleCancelClick = () => {
    setSecurityQuestions([]);
  };

  const handleAddSectionClick = () => {
    setSecurityQuestions((prevQuestions) => [...prevQuestions, { question: '', active: true }]);
  };

  const handleSectionInputChange = (index, value) => {
    setSecurityQuestions((prevQuestions) => {
      const newQuestions = [...prevQuestions];
      newQuestions[index] = { ...newQuestions[index], question: value };
      return newQuestions;
    });
  };

  const handleDeleteSectionClick = (index) => {
    setSecurityQuestions((prevQuestions) => {
      const newQuestions = [...prevQuestions];
      newQuestions[index] = { ...newQuestions[index], active: false };
      return newQuestions;
    });
  };

  const fieldsDisabled = !hasRoles(context, EDIT_SECURITY_QUESTION_ROLES);

  return (
    <div className="client-security-questions">
      <div className="client-security-questions-content">
        <h3 className="security-questions-header">Client Security Questions</h3>
        <div className="security-questions-input-container">
          <label className="input-label">Security Questions:</label>
          <div className="security-questions-input-container-column">
            {securityQuestions.map(
              (section, index) =>
                section.active && (
                  <div key={index}>
                    <input
                      className="admin-text-input"
                      type="text"
                      value={section.question}
                      disabled={fieldsDisabled}
                      onChange={({ target }) => handleSectionInputChange(index, target.value)}
                    />
                    {securityQuestions.length > 1 && (
                      <button
                        className="delete-section-button"
                        onClick={() => handleDeleteSectionClick(index)}
                        disabled={fieldsDisabled}
                      >
                        X
                      </button>
                    )}
                  </div>
                )
            )}
          </div>
          {toolTipsEnabled && (
            <div className="form-tooltips">
              <ToolTip
                tooltipExternalReference={'sections123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Security Questions:'}
              />
            </div>
          )}
        </div>
        <div className="client-security-questions-add-button">
          <SaveButton title="Add" onClick={handleAddSectionClick} color="#077187" disabled={fieldsDisabled} />
        </div>
      </div>
      {!editMode && (
        <div className={'client-security-questions-button-container'}>
          <SaveButton
            color={'#ffffff'}
            textColor={'#077187'}
            border={'1px solid #077187'}
            title={'Cancel'}
            onClick={handleCancelClick}
            disabled={fieldsDisabled}
          />
          <SaveButton
            color={'#077187'}
            title={'Save Changes'}
            onClick={handleSaveChangesClick}
            disabled={fieldsDisabled}
          />
        </div>
      )}
    </div>
  );
};
